import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Copy, FileText, Loader2, Sparkles, Trash2, ExternalLink, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { isValid } from "./useBulkImport";
import { AppointmentInput } from "@/components/loads/AppointmentInput";

const COLS = [
  ["load_number", "Broker ref #", "lg:col-span-1"],
  ["broker_name", "Broker", "col-span-2 sm:col-span-1 lg:col-span-2"],
  ["rate", "Rate (USD)", "lg:col-span-1", "number"],
  ["pickup_date", "Pickup date", "lg:col-span-1", "date"],
  ["pickup_appt", "Pickup appointment", "col-span-2 lg:col-span-2", "appt"],
  ["delivery_date", "Delivery date", "lg:col-span-1", "date"],
  ["delivery_appt", "Delivery appointment", "col-span-2 lg:col-span-2", "appt"],
  ["broker_address", "Broker address", "col-span-2 lg:col-span-6"],
  ["pickup_location", "Pickup", "col-span-2 lg:col-span-3"],
  ["delivery_location", "Delivery", "col-span-2 lg:col-span-3"],
];

const Pill = ({ tone, icon: Icon, children, testId }) => (
  <span className={`pill border ${tone}`} data-testid={testId}>{Icon && <Icon className={`w-3 h-3 ${Icon === Loader2 ? "animate-spin" : ""}`} />}{children}</span>
);

const RowStatus = ({ row, dup }) => {
  const id = row.key;
  if (row.phase === "queued") return <Pill tone="bg-slate-50 text-slate-600 border-slate-200" testId={`row-status-${id}`}>Waiting…</Pill>;
  if (row.phase === "reading") return <Pill tone="bg-blue-50 text-[#002FA7] border-blue-200" icon={Loader2} testId={`row-status-${id}`}>Reading…</Pill>;
  if (row.phase === "creating") return <Pill tone="bg-blue-50 text-[#002FA7] border-blue-200" icon={Loader2} testId={`row-status-${id}`}>Creating…</Pill>;
  if (row.phase === "created") return <Pill tone="bg-emerald-50 text-emerald-700 border-emerald-200" icon={CheckCircle2} testId={`row-status-${id}`}>Load created</Pill>;
  const pills = [];
  if (row.failed) pills.push(<Pill key="f" tone="bg-rose-50 text-rose-700 border-rose-200" icon={AlertTriangle} testId={`row-failed-${id}`}>AI couldn&apos;t read it</Pill>);
  else if (row.flagged.length) pills.push(<Pill key="l" tone="bg-amber-50 text-amber-800 border-amber-200" icon={AlertTriangle} testId={`row-flagged-${id}`}>{row.flagged.length} field{row.flagged.length === 1 ? "" : "s"} to check</Pill>);
  else if (row.edited) pills.push(<Pill key="e" tone="bg-slate-50 text-slate-700 border-slate-200" icon={CheckCircle2} testId={`row-edited-${id}`}>Edited</Pill>);
  else pills.push(<Pill key="a" tone="bg-emerald-50 text-emerald-700 border-emerald-200" icon={Sparkles} testId={`row-ai-${id}`}>High confidence</Pill>);
  if (dup) pills.push(<Pill key="d" tone="bg-rose-50 text-rose-700 border-rose-200" icon={Copy} testId={`row-duplicate-${id}`}>{dup === "batch" ? "Same load # earlier in this list" : dup === "trash" ? "Load # already exists (in Trash)" : "Load # already exists"}</Pill>);
  if (!row.failed && !isValid(row.fields)) pills.push(<Pill key="m" tone="bg-slate-50 text-slate-600 border-slate-200" testId={`row-missing-${id}`}>Missing required info</Pill>);
  return <>{pills}</>;
};

export const ImportRow = ({ row, dup, canSelect, onField, onCheck, onRemove, onPreview }) => {
  const busy = row.phase !== "ready";
  const done = row.phase === "created";
  const approved = row.checked && canSelect;
  return (
    <div className={`rounded-xl border bg-white p-4 transition-colors ${done ? "border-emerald-200 bg-emerald-50/30" : approved ? "border-emerald-400 shadow-sm" : "border-slate-200"}`} data-testid={`import-row-${row.key}`} data-approved={approved ? "true" : "false"}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onPreview} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#002FA7] hover:underline min-w-0" data-testid={`row-view-file-${row.key}`}>
              <FileText className="w-4 h-4 shrink-0" /><span className="truncate max-w-[220px] sm:max-w-xs">{row.file.name}</span>
            </button>
            <RowStatus row={row} dup={dup} />
            {done && <Link to={`/admin/loads/${row.loadId}`} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline" data-testid={`row-open-load-${row.key}`}>Open load <ExternalLink className="w-3 h-3" /></Link>}
          </div>
          {row.error && <p className={`mt-1 text-xs ${done ? "text-amber-700" : "text-rose-700"}`} data-testid={`row-error-${row.key}`}>{row.error}</p>}
          {row.notes && !done && !(row.edited && !row.flagged.length) && <p className="mt-1 text-xs italic text-slate-500" data-testid={`row-notes-${row.key}`}>AI note: {row.notes}</p>}
        </div>
        {!done && row.phase !== "creating" && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={() => onCheck(!approved)} disabled={!canSelect && !approved}
              title={canSelect ? "" : "Fix the flagged fields first"}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${approved ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500 hover:text-emerald-700"}`}
              data-testid={`row-approve-${row.key}`}>
              <Check className="w-3.5 h-3.5" /> {approved ? "Approved" : "Approve"}
            </button>
            <button type="button" onClick={onRemove} className="p-1.5 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50" aria-label={`Discard ${row.file.name}`} title="Discard" data-testid={`row-remove-${row.key}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-6 gap-3">
        {COLS.map(([k, label, span, type]) => {
          const flagged = row.phase === "ready" && row.flagged.includes(k);
          return (
            <div key={k} className={`block ${span}`}>
              <span className={`text-[11px] font-medium ${flagged ? "text-amber-800" : "text-slate-500"}`}>{label}{flagged && " · check"}</span>
              {type === "appt" ? (
                <AppointmentInput value={row.fields[k]} disabled={busy} flagged={flagged} onChange={(v) => onField(k, v)} testId={`row-${k}-${row.key}`} />
              ) : (
              <Input
                type={type || "text"}
                step={type === "number" ? "0.01" : undefined}
                value={row.fields[k]}
                disabled={busy}
                onChange={(e) => onField(k, e.target.value)}
                className={`h-9 mt-0.5 ${flagged ? "border-amber-400 bg-amber-50 focus-visible:ring-amber-400" : ""}`}
                aria-label={label}
                data-testid={`row-${k}-${row.key}`}
              />
              )}
              {k === "broker_address" && flagged && !row.fields.broker_address && <p className="mt-1 text-xs text-amber-700" data-testid={`row-address-hint-${row.key}`}>Not on the ratecon. Type the billing address; it&apos;s saved to the broker for next time.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
